import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useContacts } from './ContactContext';
import { useSettings } from './SettingsContext';
import { sendBrevoEmail, isBrevoConfigured } from '../lib/brevo';

const EmailContext = createContext();

export const useEmail = () => {
  const context = useContext(EmailContext);
  if (!context) {
    throw new Error('useEmail must be used within an EmailProvider');
  }
  return context;
};

export const EmailProvider = ({ children }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const { contacts } = useContacts();
  const { settings } = useSettings();

  const getEnvValue = (key) => {
    if (typeof import.meta !== 'undefined' && import.meta.env && key in import.meta.env) {
      return import.meta.env[key];
    }

    if (typeof process !== 'undefined' && process.env && key in process.env) {
      return process.env[key];
    }

    return '';
  };

  const defaultSenderEmail = (getEnvValue('VITE_BREVO_SENDER_EMAIL') || 'recruiting@prosperityleaders.com').trim();
  const defaultSenderName = (getEnvValue('VITE_BREVO_SENDER_NAME') || 'Prosper CRM').trim();

  const deriveSenderName = () => {
    if (!settings.emailSignature) return defaultSenderName;

    const lines = settings.emailSignature
      .split('\n')
      .map(line => line.trim())
      .filter(Boolean);

    return lines[0] || defaultSenderName;
  };

  const compileTemplate = (content = '', contact, additionalVariables = {}) => {
    if (!content) return '';

    const variables = {
      firstName: contact?.firstName || '',
      lastName: contact?.lastName || '',
      email: contact?.email || '',
      phone: contact?.phone || '',
      company: contact?.company || '',
      title: contact?.title || '',
      experience: contact?.experience || '',
      location: contact?.location || '',
      availableDay: '',
      alternativeDay: '',
      timeSlot1: '',
      timeSlot2: '',
      timeSlot3: '',
      meetingDate: '',
      meetingTime: '',
      zoomLink: '',
      dialIn: '',
      senderName: deriveSenderName(),
      senderTitle: 'Prosperity Leaders',
      ...additionalVariables
    };

    return content.replace(/{{\s*([\w.]+)\s*}}/g, (_, key) => {
      return key in variables ? variables[key] : '';
    });
  };

  const convertToHtml = (content = '') => {
    if (!content) return '';

    return content
      .split('\n')
      .map(line => line.trim())
      .map(line => (line ? `<p>${line.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>` : '<br />'))
      .join('');
  };

  // Load data from localStorage on mount
  useEffect(() => {
    const savedCampaigns = localStorage.getItem('prosper-crm-email-campaigns');
    const savedTemplates = localStorage.getItem('prosper-crm-email-templates');

    if (savedCampaigns) {
      setCampaigns(JSON.parse(savedCampaigns));
    } else {
      // Initialize with sample campaigns
      const sampleCampaigns = [
        {
          id: '1',
          name: 'Q1 2024 Agent Outreach',
          description: 'Initial outreach to experienced life insurance agents',
          type: 'single',
          status: 'completed',
          subject: 'Exciting Opportunity for Experienced Life Insurance Professionals',
          content: 'Sample content...',
          recipients: ['1', '2', '3'],
          createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
          sentAt: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
          stats: {
            sent: 150,
            delivered: 148,
            opened: 89,
            clicked: 23,
            replied: 7,
            bounced: 2,
            unsubscribed: 1
          },
          abTest: {
            enabled: true,
            subjectB: 'Transform Your Insurance Career Today',
            splitPercentage: 50,
            results: {
              openRateA: 58.7,
              openRateB: 62.3,
              winner: 'B'
            }
          }
        },
        {
          id: '2',
          name: 'Follow-up Sequence - Interested Prospects',
          description: 'Follow-up sequence for prospects who showed interest',
          type: 'sequence',
          status: 'active',
          sequence: [
            {
              id: 1,
              subject: 'Following up on our conversation',
              content: 'Sample follow-up content...',
              delay: 0,
              delayUnit: 'days'
            },
            {
              id: 2,
              subject: 'Additional information you requested',
              content: 'Sample additional info content...',
              delay: 3,
              delayUnit: 'days'
            }
          ],
          recipients: ['1', '2'],
          createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          stats: {
            sent: 45,
            delivered: 45,
            opened: 32,
            clicked: 12,
            replied: 4,
            bounced: 0,
            unsubscribed: 0
          }
        },
        {
          id: '3',
          name: 'Monthly Newsletter - Industry Updates',
          description: 'Monthly newsletter with industry insights',
          type: 'single',
          status: 'draft',
          subject: 'Industry insights that could impact your business',
          content: 'Sample newsletter content...',
          recipients: [],
          createdAt: new Date().toISOString(),
          stats: {
            sent: 0,
            delivered: 0,
            opened: 0,
            clicked: 0,
            replied: 0,
            bounced: 0,
            unsubscribed: 0
          }
        }
      ];
      setCampaigns(sampleCampaigns);
      localStorage.setItem('prosper-crm-email-campaigns', JSON.stringify(sampleCampaigns));
    }

    if (savedTemplates) {
      setTemplates(JSON.parse(savedTemplates));
    } else {
      // Initialize with sample templates
      const sampleTemplates = [
        {
          id: '1',
          name: 'Initial Outreach - Life Insurance Professionals',
          description: 'First contact email for experienced life insurance agents',
          category: 'initial_outreach',
          subject: 'Exciting Opportunity for Experienced Life Insurance Professionals',
          content: `Hi {{firstName}},

I hope this email finds you well. I'm reaching out to you because of your impressive background in life insurance with {{company}}.

We're Prosperity Leaders, and we specialize in helping top-performing agents like yourself significantly increase their earning potential while maintaining the client relationships they've worked so hard to build.

Here's what makes us different:

• 40-60% income increases in the first year for most agents
• Commission rates starting at 85% and going up to 95%
• Comprehensive training and ongoing support
• Advanced lead generation systems
• No upfront costs or franchise fees

Given your {{experience}} of experience and your current production level, I believe you'd be an excellent fit for our opportunity.

Would you be interested in a brief 15-minute call this week to learn more about how we can help you take your career to the next level?

I have some time available on {{availableDay}} or {{alternativeDay}}. Which works better for you?

Looking forward to hearing from you.

Best regards,
{{senderName}}
{{senderTitle}}
Prosperity Leaders

P.S. Many of our top agents wish they had made this move sooner. Don't let another year pass wondering "what if."`,
          usage: 25,
          isDefault: true,
          createdAt: new Date().toISOString()
        },
        {
          id: '2',
          name: 'Follow-up Email - Interested Prospects',
          description: 'Follow-up for prospects who showed initial interest',
          category: 'follow_up',
          subject: 'Following up on our conversation about your career growth',
          content: `Hi {{firstName}},

I wanted to follow up on my previous email about the opportunity with Prosperity Leaders.

I know you're busy building your business with {{company}}, but I didn't want you to miss out on learning about how our top agents are achieving remarkable growth.

Quick reminder of what we offer:
• Proven system that's helped agents increase income by 40-60%
• Higher commission rates (85-95% vs industry standard)
• Comprehensive training and support
• Advanced lead systems
• No territorial restrictions

Since you have {{experience}} of experience in life insurance, you already have the foundation for success with our system.

I have a few time slots available this week for a brief call:
• {{timeSlot1}}
• {{timeSlot2}}
• {{timeSlot3}}

Would any of these work for you? The call will only take 15 minutes, and I guarantee you'll learn something valuable about growing your business.

Best regards,
{{senderName}}`,
          usage: 18,
          isDefault: true,
          createdAt: new Date().toISOString()
        },
        {
          id: '3',
          name: 'Meeting Invitation - Overview Presentation',
          description: 'Invitation to company overview meeting',
          category: 'meeting_invitation',
          subject: 'Invitation: Exclusive Overview Meeting - Prosperity Leaders Opportunity',
          content: `Hi {{firstName}},

Thank you for your interest in learning more about the Prosperity Leaders opportunity.

I'd like to invite you to an exclusive overview meeting where you'll discover:

• Exactly how our system works and why it's so effective
• Real case studies of agents who've made the transition
• Detailed compensation breakdown and earning potential
• The support and training you'll receive
• Next steps if you decide to move forward

Meeting Details:
📅 Date: {{meetingDate}}
⏰ Time: {{meetingTime}}
🔗 Zoom Link: {{zoomLink}}
📞 Dial-in: {{dialIn}}

What to expect:
• 45-minute presentation with Q&A
• No pressure, just information
• Opportunity to speak with current agents
• Clear next steps if interested

Please confirm your attendance by replying to this email. If the scheduled time doesn't work, let me know and we can find an alternative.

I'm excited to share this opportunity with you and answer any questions you might have.

Best regards,
{{senderName}}
{{senderTitle}}
Prosperity Leaders

P.S. Space is limited, so please confirm as soon as possible.`,
          usage: 12,
          isDefault: true,
          createdAt: new Date().toISOString()
        },
        {
          id: '4',
          name: 'Nurturing Email - Industry Insights',
          description: 'Educational content to nurture prospects over time',
          category: 'nurturing',
          subject: 'Industry insights that could impact your business',
          content: `Hi {{firstName}},

I hope you're having a great week at {{company}}.

I wanted to share some interesting industry insights that might be relevant to your business:

📊 Recent Industry Trends:
• Life insurance sales are up 15% this quarter
• Consumer demand for financial protection is at an all-time high
• Top agents are leveraging new technology to scale their businesses

This growth presents incredible opportunities for experienced agents like yourself who understand the market and have strong client relationships.

At Prosperity Leaders, we've been helping agents capitalize on these trends through:
• Advanced lead generation systems
• Proven sales methodologies
• Higher commission structures
• Comprehensive training programs

Many agents in your area have already made the transition and are seeing remarkable results. For example:
• Sarah J. increased her income by 65% in her first year
• Michael R. doubled his production within 18 months
• Jennifer L. built a team of 12 agents and now earns six figures

Would you be interested in learning more about how these agents achieved such success?

I'd be happy to share more details in a brief call. No pressure, just information that could help you grow your business regardless of your current situation.

Best regards,
{{senderName}}

P.S. If you're curious about the specific strategies these agents used, I can share a detailed case study during our call.`,
          usage: 8,
          isDefault: true,
          createdAt: new Date().toISOString()
        },
        {
          id: '5',
          name: 'Re-engagement - Cold Prospects',
          description: 'Re-engage prospects who went cold',
          category: 'reengagement',
          subject: 'New developments in our opportunity - Worth another look?',
          content: `Hi {{firstName}},

It's been a while since we last spoke about the opportunity with Prosperity Leaders. I hope your business with {{company}} is going well.

I'm reaching out because we've made some exciting developments that I thought might interest you:

🆕 What's New:
• Enhanced commission structure (now up to 95%)
• New lead generation technology
• Expanded territory options
• Additional training resources
• Simplified onboarding process

Since our last conversation, we've helped over 200 agents make successful transitions, with an average income increase of 52% in their first year.

I understand timing is everything in this business, and what wasn't right before might be perfect now.

Would you be open to a brief 10-minute call to hear about these new developments? No commitment required - just sharing information that might be valuable for your career.

I have some availability:
• {{timeOption1}}
• {{timeOption2}}
• {{timeOption3}}

If none of these work, just let me know what's convenient for you.

Best regards,
{{senderName}}

P.S. Even if you're not interested in making a change right now, you might know other agents who could benefit from this opportunity. We offer referral bonuses for successful introductions.`,
          usage: 5,
          isDefault: true,
          createdAt: new Date().toISOString()
        }
      ];
      setTemplates(sampleTemplates);
      localStorage.setItem('prosper-crm-email-templates', JSON.stringify(sampleTemplates));
    }
  }, []);

  // Save to localStorage whenever data changes
  useEffect(() => {
    localStorage.setItem('prosper-crm-email-campaigns', JSON.stringify(campaigns));
  }, [campaigns]);

  useEffect(() => {
    localStorage.setItem('prosper-crm-email-templates', JSON.stringify(templates));
  }, [templates]);

  const createCampaign = (campaignData) => {
    const newCampaign = {
      id: Date.now().toString(),
      ...campaignData,
      status: 'draft',
      createdAt: new Date().toISOString(),
      stats: {
        sent: 0,
        delivered: 0,
        opened: 0,
        clicked: 0,
        replied: 0,
        bounced: 0,
        unsubscribed: 0
      }
    };

    setCampaigns(prev => [newCampaign, ...prev]);
    return newCampaign;
  };

  const updateCampaign = (id, updates) => {
    setCampaigns(prev => 
      prev.map(campaign => 
        campaign.id === id 
          ? { ...campaign, ...updates, updatedAt: new Date().toISOString() }
          : campaign
      )
    );
  };

  const deleteCampaign = (id) => {
    setCampaigns(prev => prev.filter(campaign => campaign.id !== id));
    toast.success('Campaign deleted successfully');
  };

  const sendCampaign = async (id) => {
    const campaign = campaigns.find(item => item.id === id);

    if (!campaign) {
      toast.error('Campaign not found');
      return false;
    }

    if (!isBrevoConfigured({ apiKey: settings.brevoApiKey })) {
      toast.error('Brevo API key not configured');
      return false;
    }

    const recipientContacts = contacts.filter(contact =>
      campaign.recipients.includes(contact.id) && contact.email
    );

    if (recipientContacts.length === 0) {
      toast.error('No valid recipient email addresses found');
      return false;
    }

    setLoading(true);

    try {
      const sender = {
        email: defaultSenderEmail,
        name: deriveSenderName()
      };

      const sendResults = [];
      const errors = [];

      for (const contact of recipientContacts) {
        const subject = compileTemplate(campaign.subject, contact);
        const textContent = compileTemplate(campaign.content, contact);
        const htmlContent = convertToHtml(textContent);

        try {
          const response = await sendBrevoEmail({
            subject,
            htmlContent,
            textContent,
            sender,
            to: [{
              email: contact.email,
              name: `${contact.firstName || ''} ${contact.lastName || ''}`.trim()
            }],
            tags: ['prosper-crm', `campaign:${campaign.id}`],
            overrides: {
              apiKey: settings.brevoApiKey
            }
          });

          sendResults.push({ contactId: contact.id, response });
        } catch (error) {
          console.error('Failed to send email via Brevo:', error);
          errors.push({ contactId: contact.id, error });
        }
      }

      const successCount = sendResults.length;
      const failureCount = errors.length;

      updateCampaign(id, {
        status: failureCount > 0 && successCount === 0 ? 'failed' : 'sent',
        sentAt: new Date().toISOString(),
        stats: {
          sent: recipientContacts.length,
          delivered: successCount,
          opened: campaign.stats?.opened || 0,
          clicked: campaign.stats?.clicked || 0,
          replied: campaign.stats?.replied || 0,
          bounced: failureCount,
          unsubscribed: campaign.stats?.unsubscribed || 0
        },
        lastRun: {
          successCount,
          failureCount,
          errors: errors.map(item => ({ contactId: item.contactId, message: item.error.message }))
        }
      });

      if (failureCount === 0) {
        toast.success(`Campaign sent to ${successCount} recipients`);
      } else {
        toast.error(`Campaign sent with ${failureCount} failures`);
      }

      return failureCount === 0;
    } catch (error) {
      console.error('Failed to send campaign:', error);
      toast.error(error.message || 'Failed to send campaign');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const createTemplate = (templateData) => {
    const newTemplate = {
      id: Date.now().toString(),
      ...templateData,
      usage: 0,
      isDefault: false,
      createdAt: new Date().toISOString()
    };

    setTemplates(prev => [newTemplate, ...prev]);
    return newTemplate;
  };

  const updateTemplate = (id, updates) => {
    setTemplates(prev => 
      prev.map(template => 
        template.id === id 
          ? { ...template, ...updates, updatedAt: new Date().toISOString() }
          : template
      )
    );
  };

  const deleteTemplate = (id) => {
    const template = templates.find(t => t.id === id);
    if (template && template.isDefault) {
      toast.error('Cannot delete default template');
      return;
    }
    
    setTemplates(prev => prev.filter(template => template.id !== id));
    toast.success('Template deleted successfully');
  };

  const getEmailTemplates = () => {
    return templates.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  };

  const getCampaignStats = () => {
    const stats = campaigns.reduce((acc, campaign) => {
      acc.total += 1;
      acc.sent += campaign.stats.sent;
      acc.delivered += campaign.stats.delivered;
      acc.opened += campaign.stats.opened;
      acc.clicked += campaign.stats.clicked;
      acc.replied += campaign.stats.replied;
      
      if (campaign.status === 'active') acc.active += 1;
      if (campaign.status === 'completed' || campaign.status === 'sent') acc.completed += 1;
      if (campaign.status === 'draft') acc.draft += 1;
      
      return acc;
    }, {
      total: 0,
      active: 0,
      completed: 0,
      draft: 0,
      sent: 0,
      delivered: 0,
      opened: 0,
      clicked: 0,
      replied: 0
    });

    return stats;
  };

  const getEmailAnalytics = (dateRange = '30d') => {
    // Generate mock analytics data
    const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90;
    
    const dailyStats = [];
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      dailyStats.push({
        date: date.toISOString().split('T')[0],
        sent: Math.floor(Math.random() * 50) + 20,
        opened: Math.floor(Math.random() * 30) + 10,
        clicked: Math.floor(Math.random() * 10) + 2
      });
    }

    const campaignComparison = campaigns.slice(0, 5).map(campaign => ({
      name: campaign.name,
      openRate: campaign.stats.sent > 0 ? Math.round((campaign.stats.opened / campaign.stats.sent) * 100) : 0,
      clickRate: campaign.stats.sent > 0 ? Math.round((campaign.stats.clicked / campaign.stats.sent) * 100) : 0,
      responseRate: campaign.stats.sent > 0 ? Math.round((campaign.stats.replied / campaign.stats.sent) * 100) : 0
    }));

    const topCampaigns = campaigns
      .filter(c => c.stats.sent > 0)
      .map(campaign => ({
        ...campaign,
        openRate: Math.round((campaign.stats.opened / campaign.stats.sent) * 100),
        clickRate: Math.round((campaign.stats.clicked / campaign.stats.sent) * 100),
        responseRate: Math.round((campaign.stats.replied / campaign.stats.sent) * 100),
        roi: Math.floor(Math.random() * 300) + 100
      }))
      .sort((a, b) => b.openRate - a.openRate)
      .slice(0, 10);

    const abTests = campaigns
      .filter(c => c.abTest && c.abTest.enabled)
      .map(campaign => ({
        id: campaign.id,
        campaign: campaign.name,
        subjectA: campaign.subject,
        subjectB: campaign.abTest.subjectB,
        openRateA: campaign.abTest.results?.openRateA || Math.floor(Math.random() * 30) + 20,
        openRateB: campaign.abTest.results?.openRateB || Math.floor(Math.random() * 30) + 20,
        winner: campaign.abTest.results?.winner || (Math.random() > 0.5 ? 'A' : 'B'),
        improvement: Math.floor(Math.random() * 15) + 5
      }));

    const totalStats = getCampaignStats();

    return {
      totalCampaigns: totalStats.total,
      emailsSent: totalStats.sent,
      avgOpenRate: totalStats.sent > 0 ? Math.round((totalStats.opened / totalStats.sent) * 100) : 0,
      avgClickRate: totalStats.sent > 0 ? Math.round((totalStats.clicked / totalStats.sent) * 100) : 0,
      dailyStats,
      campaignComparison,
      topCampaigns,
      abTests
    };
  };

  const value = {
    campaigns,
    templates,
    loading,
    createCampaign,
    updateCampaign,
    deleteCampaign,
    sendCampaign,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    getEmailTemplates,
    getCampaignStats,
    getEmailAnalytics
  };

  return (
    <EmailContext.Provider value={value}>
      {children}
    </EmailContext.Provider>
  );
};